import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatMYR } from "../lib/api";
import { Search, Package, Receipt, UserRound, Plus, LayoutDashboard, Ticket, HandCoins, Globe, Settings as SettingsIcon, X } from "lucide-react";

/**
 * Command Palette — Cmd+K / Ctrl+K global search.
 * Fetches products / customers / sales on open and does fuzzy prefix/substring match locally.
 */
export default function CommandPalette({ open, onOpenChange }) {
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const [q, setQ] = useState("");
    const [activeIdx, setActiveIdx] = useState(0);
    const [data, setData] = useState({ products: [], customers: [], sales: [] });
    const [loading, setLoading] = useState(false);

    // Load data whenever it opens (cheap; small collections)
    useEffect(() => {
        if (!open) return;
        setQ("");
        setActiveIdx(0);
        setLoading(true);
        Promise.all([
            api.get("/products").then((r) => r.data).catch(() => []),
            api.get("/customers").then((r) => r.data).catch(() => []),
            api.get("/sales").then((r) => r.data).catch(() => []),
        ]).then(([products, customers, sales]) => {
            setData({ products, customers, sales });
        }).finally(() => setLoading(false));
        // Focus input shortly after modal renders
        setTimeout(() => inputRef.current?.focus(), 50);
    }, [open]);

    // Quick nav shortcuts (always shown when input is empty)
    const shortcuts = [
        { icon: LayoutDashboard, label: "仪表板", to: "/", kbd: "G D" },
        { icon: Plus, label: "新建销售单", to: "/sales/new", kbd: "N S", accent: true },
        { icon: Package, label: "产品管理", to: "/products", kbd: "G P" },
        { icon: Receipt, label: "销售记录", to: "/sales", kbd: "G L" },
        { icon: UserRound, label: "客户管理", to: "/customers", kbd: "G C" },
        { icon: HandCoins, label: "分成记录", to: "/payouts", kbd: "G Y" },
        { icon: Ticket, label: "优惠码", to: "/coupons", kbd: "G K" },
        { icon: Globe, label: "网站设置 · 首页", to: "/site/home", kbd: "G W" },
        { icon: SettingsIcon, label: "系统设置", to: "/settings", kbd: "G S" },
    ];

    // Build result groups
    const results = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return { products: [], customers: [], sales: [] };
        const match = (v) => (v || "").toString().toLowerCase().includes(term);
        const products = data.products.filter((p) => match(p.name) || match(p.sku) || match(p.category)).slice(0, 6);
        const customers = data.customers.filter((c) => match(c.name) || match(c.phone) || match(c.email)).slice(0, 5);
        const sales = data.sales.filter((s) => match(s.invoice_no) || match(s.customer_name) || match(s.customer_phone)).slice(0, 5);
        return { products, customers, sales };
    }, [q, data]);

    // Flattened list for keyboard navigation
    const flat = useMemo(() => {
        if (!q.trim()) return shortcuts.map((s) => ({ kind: "nav", ...s }));
        const arr = [];
        results.products.forEach((p) => arr.push({ kind: "product", item: p }));
        results.customers.forEach((c) => arr.push({ kind: "customer", item: c }));
        results.sales.forEach((s) => arr.push({ kind: "sale", item: s }));
        return arr;
    }, [q, results, shortcuts]);

    const go = (target) => {
        onOpenChange(false);
        navigate(target);
    };

    const activate = (row) => {
        if (!row) return;
        if (row.kind === "nav") return go(row.to);
        if (row.kind === "product") return go(`/products?q=${encodeURIComponent(row.item.sku)}`);
        if (row.kind === "customer") return go(`/customers?q=${encodeURIComponent(row.item.name)}`);
        if (row.kind === "sale") return go(`/sales?q=${encodeURIComponent(row.item.invoice_no || row.item.customer_name || "")}`);
    };

    // Reset active index whenever query changes
    useEffect(() => { setActiveIdx(0); }, [q]);

    const onKeyDown = (e) => {
        if (e.key === "Escape") { e.preventDefault(); onOpenChange(false); return; }
        if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, Math.max(0, flat.length - 1))); return; }
        if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(0, i - 1)); return; }
        if (e.key === "Enter") { e.preventDefault(); activate(flat[activeIdx]); return; }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[10vh] px-4" role="dialog" data-testid="command-palette">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => onOpenChange(false)} />
            <div className="relative w-full max-w-xl bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
                <div className="flex items-center gap-3 px-4 h-14 border-b border-slate-200">
                    <Search className="h-4 w-4 text-gold-500 shrink-0" />
                    <input
                        ref={inputRef}
                        data-testid="command-palette-input"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={onKeyDown}
                        placeholder="搜索产品、客户、订单... 或跳转页面"
                        className="flex-1 bg-transparent outline-none text-slate-900 placeholder:text-slate-400 text-sm"
                    />
                    <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-[10px] font-mono text-slate-500">ESC</kbd>
                    <button onClick={() => onOpenChange(false)} className="lg:hidden text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
                </div>

                <div className="max-h-[60vh] overflow-y-auto py-2" data-testid="command-palette-list">
                    {loading && <div className="px-4 py-3 text-xs text-gold-500">加载中...</div>}

                    {!q.trim() && (
                        <div className="px-2">
                            <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-gold-500">快速跳转</div>
                            {shortcuts.map((s, idx) => {
                                const active = activeIdx === idx;
                                return (
                                    <button
                                        key={s.to}
                                        data-testid={`palette-nav-${s.to.replace(/\W+/g, "-")}`}
                                        onMouseEnter={() => setActiveIdx(idx)}
                                        onClick={() => go(s.to)}
                                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                                            active ? "bg-gold-100 text-slate-900" : "text-slate-700 hover:bg-slate-50"
                                        }`}
                                    >
                                        <s.icon className={`h-4 w-4 ${s.accent ? "text-gold-600" : "text-gold-500"}`} />
                                        <span className={`flex-1 text-left ${s.accent ? "font-semibold" : ""}`}>{s.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {q.trim() && flat.length === 0 && !loading && (
                        <div className="px-4 py-6 text-center text-sm text-gold-500">
                            没有找到与「{q}」相关的结果
                        </div>
                    )}

                    {q.trim() && results.products.length > 0 && (
                        <ResultGroup label="产品" startIdx={0} activeIdx={activeIdx} onHover={setActiveIdx}>
                            {results.products.map((p, i) => (
                                <ResultRow
                                    key={p.id}
                                    idx={i}
                                    active={activeIdx === i}
                                    onHover={() => setActiveIdx(i)}
                                    onClick={() => activate({ kind: "product", item: p })}
                                    testId={`palette-product-${p.id}`}
                                    icon={<Package className="h-3.5 w-3.5 text-gold-500" />}
                                    title={p.name}
                                    subtitle={`${p.sku} · 库存 ${p.stock} · ${formatMYR(p.unit_price)}`}
                                />
                            ))}
                        </ResultGroup>
                    )}

                    {q.trim() && results.customers.length > 0 && (
                        <ResultGroup label="客户" startIdx={results.products.length} activeIdx={activeIdx} onHover={setActiveIdx}>
                            {results.customers.map((c, i) => {
                                const idx = results.products.length + i;
                                return (
                                    <ResultRow
                                        key={c.id}
                                        idx={idx}
                                        active={activeIdx === idx}
                                        onHover={() => setActiveIdx(idx)}
                                        onClick={() => activate({ kind: "customer", item: c })}
                                        testId={`palette-customer-${c.id}`}
                                        icon={<UserRound className="h-3.5 w-3.5 text-gold-500" />}
                                        title={c.name}
                                        subtitle={`${c.phone || "无电话"}${c.tier ? ` · ${c.tier === "diamond" ? "钻卡" : c.tier === "gold" ? "金卡" : "银卡"}` : ""} · 累计 ${formatMYR(c.total_spent || 0)}`}
                                    />
                                );
                            })}
                        </ResultGroup>
                    )}

                    {q.trim() && results.sales.length > 0 && (
                        <ResultGroup label="订单" startIdx={results.products.length + results.customers.length} activeIdx={activeIdx} onHover={setActiveIdx}>
                            {results.sales.map((s, i) => {
                                const idx = results.products.length + results.customers.length + i;
                                return (
                                    <ResultRow
                                        key={s.id}
                                        idx={idx}
                                        active={activeIdx === idx}
                                        onHover={() => setActiveIdx(idx)}
                                        onClick={() => activate({ kind: "sale", item: s })}
                                        testId={`palette-sale-${s.id}`}
                                        icon={<Receipt className="h-3.5 w-3.5 text-gold-500" />}
                                        title={`${s.invoice_no || "无编号"} · ${s.customer_name || "游客"}`}
                                        subtitle={`${(s.created_at || "").slice(0, 10)} · ${formatMYR(s.total || 0)}`}
                                    />
                                );
                            })}
                        </ResultGroup>
                    )}
                </div>

                <div className="border-t border-slate-200 bg-slate-50 px-4 py-2 flex items-center justify-between text-[10px] text-slate-500 font-mono">
                    <span><kbd className="px-1 rounded bg-white border border-slate-200">↑↓</kbd> 移动 · <kbd className="px-1 rounded bg-white border border-slate-200">↵</kbd> 打开</span>
                    <span>⌘K / Ctrl+K</span>
                </div>
            </div>
        </div>
    );
}

function ResultGroup({ label, children }) {
    return (
        <div className="px-2 mt-1">
            <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-gold-500">{label}</div>
            {children}
        </div>
    );
}

function ResultRow({ active, onHover, onClick, icon, title, subtitle, testId }) {
    return (
        <button
            data-testid={testId}
            onMouseEnter={onHover}
            onClick={onClick}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                active ? "bg-gold-100 text-slate-900" : "text-slate-700 hover:bg-slate-50"
            }`}
        >
            <span className="h-6 w-6 rounded bg-white border border-slate-200 flex items-center justify-center shrink-0">{icon}</span>
            <span className="flex-1 min-w-0 text-left">
                <span className="block truncate font-medium">{title}</span>
                <span className="block truncate text-xs text-gold-500 font-mono">{subtitle}</span>
            </span>
        </button>
    );
}
